import { useMutation, useQuery } from '@tanstack/react-query';
import type { ReferralFacilityView } from '@wodi/shared';
import { Button, Notice, SearchPicker, TextField, type SearchPickerStatus } from '@wodi/ui';
import { useEffect, useState } from 'react';

import { ApiError } from '../api/client.ts';
import { searchReferralFacilities, searchRegistryFacilities } from '../api/referrals.ts';
import { useOnline } from '../hooks/use-online.ts';
import styles from './referrals.module.css';

// Choosing the other facility (docs/plans/phase2-referrals.md §1.1 item 2): type to search
// Wodi's copy of the national Facility Registry; if it isn't there, search the national
// registry itself (those who may), and if it still isn't found, type the name. A typed
// facility is kept as typed and marked "Typed; not checked" everywhere it shows.

const MIN_CHARS = 2;

export type FacilityChoice =
  | { kind: 'registry'; facility: ReferralFacilityView }
  | { kind: 'typed'; name: string; mflCode: string };

/** What the API takes, from the choice. */
export function facilityFields(choice: FacilityChoice | null): {
  registryFacilityId: string | null;
  name: string;
  mflCode: string | null;
} {
  if (choice === null) return { registryFacilityId: null, name: '', mflCode: null };
  if (choice.kind === 'registry') {
    return {
      registryFacilityId: choice.facility.id,
      name: choice.facility.name,
      mflCode: choice.facility.mflCode,
    };
  }
  const mfl = choice.mflCode.trim();
  return { registryFacilityId: null, name: choice.name.trim(), mflCode: mfl === '' ? null : mfl };
}

function useDebounced(value: string, ms: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebounced(value);
    }, ms);
    return () => {
      window.clearTimeout(timer);
    };
  }, [value, ms]);
  return debounced;
}

function facilityMeta(f: ReferralFacilityView): string {
  return [f.levelDisplay, f.county, f.subCounty].filter((v) => v !== null).join(', ');
}

/** "TEST-00003" or "12345" reads as an MFL code; anything with a space as a name. */
function looksLikeCode(text: string): boolean {
  return /^[A-Za-z0-9-]+$/.test(text) && /\d/.test(text);
}

export function FacilityPicker({
  label,
  value,
  onChange,
  mayRegistrySearch,
  errorMessage,
}: {
  label: string;
  value: FacilityChoice | null;
  onChange: (next: FacilityChoice | null) => void;
  /** registries.search: may search the national registry from here. */
  mayRegistrySearch: boolean;
  errorMessage?: string | undefined;
}) {
  const online = useOnline();
  const [text, setText] = useState('');
  const [registryItems, setRegistryItems] = useState<{
    query: string;
    items: ReferralFacilityView[];
  } | null>(null);
  const trimmed = text.trim();
  const debounced = useDebounced(trimmed, 200);
  const cache = useQuery({
    queryKey: ['referral-facilities', debounced],
    queryFn: () => searchReferralFacilities(debounced),
    enabled: online && debounced.length >= MIN_CHARS && value === null,
    staleTime: 10_000,
    retry: false,
  });
  const registry = useMutation({
    mutationFn: (q: string) =>
      searchRegistryFacilities(looksLikeCode(q) ? { mflCode: q } : { name: q }),
    onSuccess: (data, q) => {
      setRegistryItems({ query: q, items: data.items });
    },
  });

  if (value?.kind === 'registry') {
    const f = value.facility;
    return (
      <div className={styles.chosen} role="group" aria-label={label}>
        <p className={styles.label}>{label}</p>
        <p className={styles.chosenName}>{f.name}</p>
        <p className={styles.meta}>
          {f.mflCode === null ? null : (
            <>
              {'MFL '}
              <span className="wodi-id">{f.mflCode}</span>
              {'. '}
            </>
          )}
          {facilityMeta(f)}
          {facilityMeta(f) === '' ? '' : '. '}
          From the national registry.
        </p>
        <Button
          dense
          onPress={() => {
            onChange(null);
          }}
        >
          Change facility
        </Button>
      </div>
    );
  }

  if (value?.kind === 'typed') {
    return (
      <div className={styles.typed} role="group" aria-label={label}>
        <TextField
          label={label}
          description="Typed; not checked against the national registry."
          value={value.name}
          onChange={(name) => {
            onChange({ ...value, name });
          }}
          isRequired
          {...(errorMessage === undefined ? {} : { errorMessage })}
        />
        <TextField
          label="MFL code (optional)"
          value={value.mflCode}
          onChange={(mflCode) => {
            onChange({ ...value, mflCode });
          }}
        />
        <Button
          variant="quiet"
          onPress={() => {
            onChange(null);
          }}
        >
          Choose from the list instead
        </Button>
      </div>
    );
  }

  const showRegistry = registryItems !== null && registryItems.query === trimmed;
  let status: SearchPickerStatus;
  if (trimmed.length < MIN_CHARS) status = 'idle';
  else if (!online) status = 'offline';
  else if (showRegistry) status = 'ready';
  else if (debounced !== trimmed || cache.isFetching || cache.isPending) status = 'searching';
  else if (cache.isError) status = 'error';
  else status = 'ready';
  const items = showRegistry ? registryItems.items : (cache.data?.items ?? []);

  const typeInstead = (
    <Button
      variant="quiet"
      onPress={() => {
        onChange({ kind: 'typed', name: trimmed, mflCode: '' });
      }}
    >
      Not listed: type the name
    </Button>
  );

  return (
    <div className={styles.picker}>
      <SearchPicker
        label={label}
        description="Type part of the name or the MFL code."
        placeholder="Facility name or MFL code"
        inputValue={text}
        onInputChange={(next) => {
          setText(next);
          if (registryItems !== null && next.trim() !== registryItems.query) setRegistryItems(null);
        }}
        status={status}
        items={items}
        itemsQuery={showRegistry ? registryItems.query : debounced}
        getKey={(f) => f.id}
        getTextValue={(f) => `${f.name} ${f.mflCode ?? ''}`}
        renderItem={(f) => (
          <span className={styles.pickerRow}>
            <span className={styles.pickerName}>{f.name}</span>
            <span className={styles.meta}>
              {f.mflCode === null ? null : <span className="wodi-id">{f.mflCode}</span>}
              {` ${facilityMeta(f)}`}
            </span>
          </span>
        )}
        isExactMatch={(f, q) => f.mflCode?.toUpperCase() === q.toUpperCase()}
        onSelect={(f) => {
          setText('');
          setRegistryItems(null);
          onChange({ kind: 'registry', facility: f });
        }}
        promptMessage="Type at least two letters of the facility's name, or its MFL code."
        emptyMessage={
          showRegistry
            ? 'The national registry has no facility that matches. Check the spelling, or type the name.'
            : mayRegistrySearch
              ? "No facility in Wodi's list matches. Search the national registry, or type the name."
              : "No facility in Wodi's list matches. Type the name."
        }
        {...(cache.error === null
          ? {}
          : {
              errorMessage:
                cache.error instanceof ApiError
                  ? cache.error.message
                  : 'Search did not work. Try again.',
            })}
        onRetry={() => void cache.refetch()}
        offlineMessage="Searching facilities needs a connection to Wodi. Type the name instead."
        countMessage={(n) => `${String(n)} ${n === 1 ? 'facility' : 'facilities'} found`}
      />
      {errorMessage === undefined ? null : (
        <p className={styles.fieldError} role="alert">
          {errorMessage}
        </p>
      )}
      <div className={styles.actions}>
        {mayRegistrySearch ? (
          <Button
            dense
            isDisabled={!online || trimmed.length < MIN_CHARS}
            isPending={registry.isPending}
            pendingLabel="Searching…"
            onPress={() => {
              registry.mutate(trimmed);
            }}
          >
            Search the national registry
          </Button>
        ) : null}
        {typeInstead}
      </div>
      {registry.isError ? (
        <Notice tone="error">
          {registry.error instanceof ApiError
            ? `${registry.error.message} You can type the name instead.`
            : "The national registry couldn't be searched. Type the name instead."}
        </Notice>
      ) : null}
    </div>
  );
}
